import { useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import AppLayout from "layouts/app-layout"
import moment from "moment/moment"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"

import Create from "pages/cashiers/create"
import Edit from "pages/cashiers/edit"
import Delete from "pages/cashiers/delete"
import Filter from "pages/cashiers/filter"
import ResetPin from "pages/cashiers/reset-pin"
import useCashiers from "repositories/cashier"

export default function List() {
    const { can } = useAuth()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const page = searchParams.get("page")
    const search = searchParams.get("search")
    const organization_id = searchParams.get("organization_id")
    const branch_id = searchParams.get("branch_id")
    const is_active = searchParams.get("is_active")
    const from = searchParams.get("from")
    const to = searchParams.get("to")

    const searchInput = useRef()

    const {
        data: cashiers,
        isLoading,
        mutate,
    } = useCashiers({
        page,
        search,
        organization_id,
        branch_id,
        is_active,
        from,
        to,
    })

    useEffect(() => {
        searchInput.current?.focus()
    }, [search])

    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoading) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [cashiers, isLoading])

    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    const handleSuccess = () => {
        mutate()
        nProgress.done()
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                strokeWidth={1}
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>

                        <input
                            ref={searchInput}
                            onChange={(e) => setSearchParams({ search: e.target.value })}
                            value={searchParams.get("search") || ""}
                            type="text"
                            placeholder="Search cashier"
                            autoComplete="off"
                            className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        />
                    </div>

                    <Filter
                        onSubmit={updateFilter}
                        onRemove={removeFilter}
                        data={Object.fromEntries(
                            Object.entries({
                                organization_id,
                                branch_id,
                                is_active,
                                from,
                                to,
                            }).filter(([_, v]) => v != null)
                        )}
                    />
                </div>

                {can("cashier.create") && (
                    <div>
                        <Create success={handleSuccess} />
                    </div>
                )}
            </div>

            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                    <thead className="bg-neutral-50 rounded-t-3xl">
                        <tr>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                                Branch
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                                Cashier Name
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                                PIN
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                                Status
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                                Created At
                            </th>
                            <th className="relative px-6 py-3">
                                <span className="sr-only">Action</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-neutral-200">
                        {isLoading && (
                            <tr className="text-center">
                                <td colSpan="6" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    Loading data...
                                </td>
                            </tr>
                        )}

                        {cashiers?.data?.length === 0 && !search && !isLoading && (
                            <tr className="text-center">
                                <td colSpan="6" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    No data
                                </td>
                            </tr>
                        )}

                        {cashiers?.data?.length === 0 && search && !isLoading && (
                            <tr className="text-center">
                                <td colSpan="6" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    No result
                                </td>
                            </tr>
                        )}

                        {cashiers?.data?.length > 0 &&
                            cashiers.data.map((row) => (
                                <tr key={row.id}>
                                    <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                        {row.branch?.name || "-"}
                                    </td>

                                    <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                        {row.name || "-"}
                                    </td>

                                    <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                        ••••••
                                    </td>

                                    <td className="px-6 py-4 text-xs whitespace-nowrap">
                                        <span
                                            className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium ${
                                                Number(row.is_active) === 1
                                                    ? "bg-emerald-50 text-emerald-700"
                                                    : "bg-neutral-100 text-neutral-600"
                                            }`}
                                        >
                                            {Number(row.is_active) === 1 ? "Active" : "Inactive"}
                                        </span>
                                    </td>

                                    <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                        {row.created_at
                                            ? moment(row.created_at).format("MMMM D, YYYY")
                                            : "-"}
                                    </td>

                                    <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                        <div className="inline-flex items-center space-x-2">
                                            {can("cashier.update") && (
                                                <>
                                                    <Edit data={row} onSuccess={handleSuccess} />
                                                    <ResetPin data={row} onSuccess={handleSuccess} />
                                                </>
                                            )}

                                            {can("cashier.delete") && (
                                                <Delete data={row} onSuccess={handleSuccess} />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                links={cashiers?.links}
                from={cashiers?.from}
                to={cashiers?.to}
                total={cashiers?.total}
            />
        </div>
    )
}

List.layout = (page) => <AppLayout children={page} title="Cashiers" />